// pages/admin/RewardSettingsManagement.tsx

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  InputAdornment,
  Divider,
  Paper,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Refresh,
  NavigateNext,
  Home,
  Close,
  Search,
  Settings as SettingsIcon,
  TrendingUp,
  TrendingDown,
  Stars,
  MonetizationOn,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListRewardSettingsQuery,
  useAddRewardSettingMutation,
  useUpdateRewardSettingMutation,
  useDeleteRewardSettingMutation,
  RewardSetting,
  RewardSettingFormData,
  validateAmount,
  validatePoints,
  validateAmountRange,
} from '../../features/admin/rewardSettingsApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
};

const RewardSettingsManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Filter states
  const [searchStr, setSearchStr] = useState('');

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<RewardSetting | null>(null);
  const [formData, setFormData] = useState<RewardSettingFormData>({
    min_amount: '',
    max_amount: '',
    points: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListRewardSettingsQuery();

  const [addRewardSetting, { isLoading: isAdding }] = useAddRewardSettingMutation();
  const [updateRewardSetting, { isLoading: isUpdating }] = useUpdateRewardSettingMutation();
  const [deleteRewardSetting, { isLoading: isDeleting }] = useDeleteRewardSettingMutation();

  // Handle API response structure
  const rewardSettings = data?.data || [];
  const totalRecords = rewardSettings.length;

  const isEditing = Boolean(editingSetting);
  const isFormLoading = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      min_amount: '',
      max_amount: '',
      points: '',
    });
    setFormErrors({});
    setEditingSetting(null);
  };

  const handleAddSetting = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleEditSetting = (setting: RewardSetting) => {
    setFormData({
      min_amount: setting.min_amount || '',
      max_amount: setting.max_amount || '',
      points: setting.points || '',
      id: setting.id,
    });
    setEditingSetting(setting);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.min_amount.trim()) {
      newErrors.min_amount = 'Minimum amount is required';
    } else if (!validateAmount(formData.min_amount)) {
      newErrors.min_amount = 'Invalid minimum amount (must be a positive number)';
    }

    if (!formData.max_amount.trim()) {
      newErrors.max_amount = 'Maximum amount is required';
    } else if (!validateAmount(formData.max_amount)) {
      newErrors.max_amount = 'Invalid maximum amount (must be a positive number)';
    }

    if (formData.min_amount && formData.max_amount && 
        !validateAmountRange(formData.min_amount, formData.max_amount)) {
      newErrors.max_amount = 'Maximum amount must be greater than minimum amount';
    }

    if (!formData.points.trim()) {
      newErrors.points = 'Points are required';
    } else if (!validatePoints(formData.points)) {
      newErrors.points = 'Invalid points (must be a positive number)';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let result;
      if (isEditing && editingSetting) {
        const updateData = {
          id: formData.id || editingSetting.id,
          min_amount: formData.min_amount.trim(),
          max_amount: formData.max_amount.trim(),
          points: formData.points.trim(),
        };
        
        result = await updateRewardSetting(updateData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward setting updated successfully!',
          severity: 'success'
        });
      } else {
        const addData = {
          min_amount: formData.min_amount.trim(),
          max_amount: formData.max_amount.trim(),
          points: formData.points.trim(),
        };
        
        result = await addRewardSetting(addData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward setting created successfully!',
          severity: 'success'
        });
      }
      handleFormClose();
      refetch();
    } catch (err: any) {
      console.error('Failed to save reward setting:', err);

      let errorMessage = `Error ${isEditing ? 'updating' : 'creating'} reward setting. Please try again.`;

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this reward setting?')) {
      try {
        const result = await deleteRewardSetting({ id }).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward setting deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete reward setting:', err);

        let errorMessage = 'Error deleting reward setting. Please try again.';

        if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.data?.error) {
          errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view reward settings.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No reward settings found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load reward settings. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  // Filter settings based on search
  const filteredSettings = rewardSettings.filter((setting: RewardSetting) => {
    const matchesSearch = !searchStr || 
      setting.points?.toLowerCase().includes(searchStr.toLowerCase()) ||
      setting.min_amount?.toLowerCase().includes(searchStr.toLowerCase()) ||
      setting.max_amount?.toLowerCase().includes(searchStr.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Users
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Reward Settings
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Reward Settings
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Configure reward point tiers based on purchase amounts
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSetting}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Add setting
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search by points or amount..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredSettings.length} setting{filteredSettings.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading reward settings...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredSettings.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <SettingsIcon sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No settings found' : 'No reward settings yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr 
                    ? `No settings match your search "${searchStr}"`
                    : 'Configure your first reward tier to get started'
                  }
                </Typography>
                {!searchStr && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddSetting}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Add setting
                  </Button>
                )}
              </Card>
            )}

            {/* Settings List */}
            {!isLoading && filteredSettings.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredSettings.map((setting: RewardSetting, index: number) => (
                    <ListItem
                      key={setting.id}
                      sx={{
                        borderBottom: index < filteredSettings.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: shopifyColors.purple,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <SettingsIcon />
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Paper
                              elevation={0}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                backgroundColor: `${shopifyColors.gold}20`,
                                borderRadius: '6px',
                              }}
                            >
                              <Stars sx={{ fontSize: 18, color: shopifyColors.gold }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                                {setting.points} Points
                              </Typography>
                            </Paper>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingUp sx={{ fontSize: 16, color: shopifyColors.success }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Min: ${setting.min_amount}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingDown sx={{ fontSize: 16, color: shopifyColors.error }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Max: ${setting.max_amount}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                              ID: {setting.id}
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEditSetting(setting)}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDelete(setting.id)}
                              disabled={isDeleting}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit reward setting' : 'Add reward setting'}
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Amount Range */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Purchase Amount Range
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="min_amount"
                      label="Minimum Amount"
                      value={formData.min_amount}
                      onChange={handleInputChange}
                      error={!!formErrors.min_amount}
                      helperText={formErrors.min_amount || "Lower bound"}
                      fullWidth
                      required
                      placeholder="1000"
                      disabled={isFormLoading}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MonetizationOn sx={{ fontSize: 20, color: shopifyColors.success }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="max_amount"
                      label="Maximum Amount"
                      value={formData.max_amount}
                      onChange={handleInputChange}
                      error={!!formErrors.max_amount}
                      helperText={formErrors.max_amount || "Upper bound"}
                      fullWidth
                      required
                      placeholder="9999"
                      disabled={isFormLoading}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MonetizationOn sx={{ fontSize: 20, color: shopifyColors.error }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Points */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Reward Points
                </Typography>
                
                <TextField
                  name="points"
                  label="Points"
                  value={formData.points}
                  onChange={handleInputChange}
                  error={!!formErrors.points}
                  helperText={formErrors.points || "Points earned in this tier"}
                  fullWidth
                  required
                  placeholder="100"
                  disabled={isFormLoading}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Stars sx={{ fontSize: 20, color: shopifyColors.gold }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />

                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 1, display: 'block' }}>
                  💡 Tip: Customers who spend between the min and max amounts will earn these points
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !formData.min_amount?.trim() || !formData.max_amount?.trim() || !formData.points?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </Box>
              ) : (
                isEditing ? 'Save changes' : 'Add setting'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RewardSettingsManagement;